<script>
import BaseAvatar from "@/components/BaseAvatar/BaseAvatar.vue";
import i18n from "@/i18n";
import {mapActions} from "pinia";
import {shareStore} from "@/store/shareStore";
export default {
  name: "OrgProfileGeneralInfo",
  components: {
    BaseAvatar
  },
  props: {
    profileInfo: {
      type: Object
    },
  },
  data() {
    return {
      profileViewFullMode: true
    }
  },
  computed: {
    bgImg() {
      const src = `/viewheroimg?modelId=${this.profileInfo.id}&modelType=ORGANIZATION`;
      return {
        'background-image': `url("${src}")`,
      };
    },
    profileAvatarSrc() {
      return `/viewimage?type=organization&sh=${this.profileInfo.largeImgHash}`;
    },
    profileAboutPrivate() {
      return !this.profileInfo.aboutPublic;
    },
    aboutTextEmpty() {
      return `${this.profileInfo.name} ${i18n.global.t('profile.hasntEnteredAboutMe')}`;
    },
    orgType() {
      let orgTypeText = '';

      if (this.profileInfo.type === 'PARTY') {
        orgTypeText = i18n.global.t('profile.orgType.party');
      } else if (this.profileInfo.type === 'YOUTH_ORGANIZATION') {
        orgTypeText = i18n.global.t('profile.orgType.youthOrganization');
      } else if (this.profileInfo.type === 'NON_GOVERNMENT_ORGANIZATION') {
        orgTypeText = i18n.global.t('profile.orgType.nonGovernmentalOrg');
      } else {
        orgTypeText = i18n.global.t('profile.orgType.other');
      }

      return orgTypeText;
    }
  },
  methods: {
    ...mapActions(shareStore, [
      'toggleShareModal',
    ]),
    toggleProfileViewMode() {
      this.profileViewFullMode = !this.profileViewFullMode;
    },
    shareProfile() {
      this.toggleShareModal();
    }
  }
}
</script>

<template>
  <div class="profile-general-info-wrap">
    <div v-if="profileViewFullMode" class="profile-bg-block">
      <div class="profile-bg-img" :style="bgImg"></div>
    </div>
    <div class="profile-main-data" :class="{'profile-main-data--minimized': !profileViewFullMode}">
      <div class="profile-info">
        <base-avatar v-show="profileViewFullMode"
                     size="125"
                     class="profile-avatar"
                     :img-src="profileAvatarSrc"
        />
        <base-avatar v-show="!profileViewFullMode"
                     size="75"
                     class="profile-avatar"
                     :img-src="profileAvatarSrc"
        />
        <div class="profile-data">
          <div class="d-flex align-item-center mb-10">
            <span class="profile-title">{{this.profileInfo.name}}</span>
          </div>
          <div v-if="profileViewFullMode" class="d-flex">
            <div class="profile-data-items-list">
              <div class="profile-data-item profile-data-item--party">
                <i class="profile-data-item__icon"></i>
                <span class="profile-data-item__text">
                  {{ orgType }}
                </span>
              </div>
              <div class="profile-data-item profile-data-item--forum">
                <i class="profile-data-item__icon"></i>
                <span class="profile-data-item__text">
                  {{this.profileInfo.forum.name}}
                </span>
              </div>
              <div class="profile-data-item profile-data-item--pic">
                <i class="profile-data-item__icon"></i>
                <base-avatar class="mr-15" :img-hash="profileInfo.largeImgHash" :size="54"/>
                <base-avatar :img-hash="profileInfo.imageHash" :size="24"/>
              </div>
            </div>
          </div>
        </div>
        <div v-if="profileViewFullMode" class="profile-about">
          <span class="profile-about__title">{{$t('profile.aboutOrg')}}</span>
          <template v-if="profileInfo.about">
            <template v-if="!profileAboutPrivate">
              <div class="profile-about__text">
                {{ profileInfo.about }}
              </div>
            </template>
            <template v-else>
              <div>
                <span class="private-label">{{$t('privacy.private')}}</span>
              </div>
            </template>
          </template>
          <template v-else>
            <div class="profile-about__no-data">
              {{aboutTextEmpty}}
            </div>
          </template>
        </div>
      </div>
      <div class="profile-controls">
        <div class="profile-view-mode-switcher" @click="toggleProfileViewMode()">
          <span class="profile-view-mode-switcher__text">
            <template v-if="profileViewFullMode">{{$t('profile.minimizeProfile')}}</template>
            <template v-else>{{$t('profile.maximizeProfile')}}</template>
          </span>
          <font-awesome-icon v-if="profileViewFullMode" :icon="['fas', 'chevron-up']" class="profile-view-mode-switcher__icon"/>
          <font-awesome-icon v-else :icon="['fas', 'chevron-down']" class="profile-view-mode-switcher__icon"/>
        </div>
        <div class="share-control" @click="shareProfile">
          <font-awesome-icon :icon="['fas', 'share-nodes']"
                             class="share-control__icon"
          />
          <span class="share-control__label">{{$t('share.share')}}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-general-info-wrap {
  width: 970px;
  margin-bottom: 10px;

  .profile-bg-block {
    position: relative;

    .profile-bg-img {
      width: 100%;
      height: 225px;
      background: #ccc;
      background-size: 100%;
      border-radius: 24px;
    }
  }

  .profile-main-data {
    display: flex;
    justify-content: space-between;
    min-height: 250px;
    padding: 0 25px 20px 20px;
    position: relative;
    background: #fff;

    .profile-view-mode-switcher {
      position: absolute;
      top: -13px;
      right: 12px;
      width: 150px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 13px;
      background-color: #a163cd;
      cursor: pointer;
      &__text {
        margin-right: 5px;
        font-size: 14px;
        font-weight: 600;
        color: #FFFFFF;
      }
      &__icon {
        color: #FFFFFF;
      }
    }

    .profile-info {
      display: flex;

      .profile-avatar {
        position: absolute;
        left: 35px;
        top: -35px;
        margin-right: 25px;
      }

      .profile-elected-info {
        position: absolute;
        top: 85px;
      }

      .profile-data {
        position: absolute;
        top: 5px;
        left: 175px;
        padding: 10px;
        .profile-title {
          display: block;
          margin: 0 10px 0 5px;
          font-size: 20px;
          font-weight: 600;
          color: #000000;
        }

        .profile-data-items-list {
          min-width: 135px;
        }

        .profile-data-item {
          display: flex;
          align-items: center;
          margin-bottom: 5px;
          &__icon {
            display: block;
            width: 25px;
            height: 20px;
            margin-right: 5px;
          }
          &__text {

          }

          &--party {
            .profile-data-item__icon {
              background: url("~@/assets/img/profile/icon_group.svg") no-repeat center;
            }
          }
          &--forum {
            .profile-data-item__icon {
              background: url("~@/assets/img/profile/icon_forum.svg") no-repeat center;
            }
          }
          &--pic {
            .profile-data-item__icon {
              background: url("~@/assets/img/profile/icon_pic.svg") no-repeat center;
            }
          }
        }
      }

      .profile-about {
        position: relative;
        margin: 165px 0 0 35px;
        width: 550px;
        text-align: center;

        &__title {
          display: inline-block;
          width: 100%;
          margin-bottom: 10px;
          color: #000;
          text-align: left;
        }
        &__text {
          color: #4f4f4f;
          text-align: left;
        }
        &__no-data {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 70px;
          color: #bbb;
        }
      }

      .private-label {
        color: #cccccc;
      }

    }

    .profile-controls {
      padding-top: 35px;

      .share-control {
        display: flex;
        align-items: center;
        height: 42px;
        padding: 0 7px;
        color: #7835a6;
        border-radius: 4px;
        background: 0 0;
        border: none;
        cursor: pointer;
        &__icon {
          margin-right: 5px;
        }
        &__label {
          font-size: 16px;
          font-weight: 600;
        }
      }
    }

    &--minimized {
      height: 105px;
      min-height: auto;
      align-items: center;
      padding-bottom: 0;
      background-image: linear-gradient(to bottom, #a163cd, #c096de);
      border-radius: 8px;

      .profile-info {
        .profile-avatar {
          position: relative;
          top: auto;
          left: auto;
        }

        .profile-data {
          left: 95px;
          top: 15px;
          .profile-title {
            font-size: 16px;
            color: #fff;
          }
        }

        .profile-elected-info {
          left: 105px;
          top: 50px
        }

      }

      .profile-controls {
        .profile-view-mode-switcher {
          position: relative;
          top: auto;
          right: auto;
          background: #fff;
          &__text {
            color: #7835a6;
          }
          &__icon {
            color: #7835a6;
          }
        }

        .share-control {
          color: #fff;
        }
      }
    }
  }

}
</style>